import { Component, effect, inject, input, model, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { WalkthroughService } from '@app/core/services/walkthrough.service';
import { AideService } from '../../aide.service';
import { AideArticle, AideRubrique } from '../../aide.models';
import { AideContenuComponent } from '../aide-contenu/aide-contenu.component';
import { AideAvisComponent } from '../aide-avis/aide-avis.component';
import { AideRechercheComponent } from '../aide-recherche/aide-recherche.component';

type EtatArticle = 'chargement' | 'pret' | 'introuvable' | 'indisponible';

/**
 * Panneau d'aide à droite. Ouvert sur le tuto de l'écran affiché s'il y en a
 * un, sinon sur la recherche et les tutos de la rubrique du module. Propose
 * la visite guidée de l'écran quand elle existe [D6].
 *
 * `simple` (interface SOLO) : ni visite guidée ni « Ouvrir en grand », qui
 * mènent aux écrans du BO complet. Ouvert aussi par AideService.ouvrirPanneau().
 */
@Component({
  selector: 'app-aide-panneau',
  standalone: true,
  imports: [DrawerModule, ButtonModule, SkeletonModule, AideContenuComponent, AideAvisComponent, AideRechercheComponent],
  templateUrl: './aide-panneau.component.html',
  styleUrl: './aide-panneau.component.scss',
})
export class AidePanneauComponent {
  private readonly router = inject(Router);
  private readonly walkthrough = inject(WalkthroughService);
  readonly aide = inject(AideService);

  readonly visible = model(false);
  readonly simple = input(false);

  readonly article = signal<AideArticle | null>(null);
  readonly etatArticle = signal<EtatArticle>('pret');
  readonly rubrique = signal<AideRubrique | null>(null);
  readonly tour = signal<string | null>(null);

  constructor() {
    // Seules les demandes faites après la création du panneau l'ouvrent.
    let vu = untracked(() => this.aide.demandeOuverture());
    effect(() => {
      const demande = this.aide.demandeOuverture();
      if (demande !== vu) {
        vu = demande;
        untracked(() => this.ouvrir());
      }
    });
  }

  /** Ouvre le panneau pour l'écran affiché. */
  ouvrir(): void {
    const url = this.router.url;
    this.tour.set(this.walkthrough.tourPourUrl(url));
    this.article.set(null);
    this.etatArticle.set('pret');
    this.visible.set(true);

    this.aide.chargerSommaire().subscribe(() => {
      this.rubrique.set(this.aide.rubriquePourUrl(url));
      const entree = this.aide.articlePourUrl(url);
      if (entree) {
        this.afficher(entree.slug);
      }
    });
  }

  afficher(slug: string): void {
    this.etatArticle.set('chargement');
    this.aide.article(slug).subscribe({
      next: (article) => {
        this.article.set(article);
        this.etatArticle.set(article ? 'pret' : 'introuvable');
      },
      error: () => this.etatArticle.set('indisponible'),
    });
  }

  retour(): void {
    this.article.set(null);
    this.etatArticle.set('pret');
  }

  ouvrirEnGrand(): void {
    const slug = this.article()?.slug;
    this.quitterVers(slug ? `/aide/${slug}` : '/aide');
  }

  /** Lien d'un tuto vers un écran : le panneau se ferme sur l'écran demandé. */
  allerA(url: string): void {
    this.quitterVers(url);
  }

  lancerVisite(): void {
    this.visible.set(false);
    // Laisse le panneau se refermer avant que la visite ne cible les éléments de l'écran.
    setTimeout(() => this.walkthrough.launch(this.tour() ?? 'global'), 300);
  }

  /**
   * Navigue PUIS ferme. Fermer d'abord laissait le voile du p-drawer (posé sur
   * <body>) orphelin quand la navigation détruit le panneau pendant son
   * animation de fermeture (ex. /moni/… → /aide : autre gabarit) : un calque
   * transparent bloquait alors tous les clics de la page. Détruit ouvert, le
   * p-drawer retire son voile lui-même.
   */
  private quitterVers(url: string): void {
    this.router.navigateByUrl(url).finally(() => this.visible.set(false));
  }
}
